// Horizontal Sections Component - Handles all horizontal sections rendering in the editor
import { Edit3, Trash2, Plus } from 'lucide-react';
import ReorderButtons from './ReorderButtons';
import { cssVars, getTagColor } from './editorConstants';
import { CardExtrasDisplay } from './CardExtras';

export default function HorizontalSections({
  horizontalOrder,
  announcementCard,
  investmentThesisCard,
  statsCard,
  howIInvestCard,
  investmentFocusCard,
  postInvestmentCard,
  highlightsCard,
  carouselCard,
  emptyCards = [],
  connect,
  onMoveSection,
  onEditAnnouncement,
  onEditInvestmentThesis,
  onEditStats,
  onEditHowIInvest,
  onEditInvestmentFocus,
  onEditPostInvestment,
  onEditHighlights,
  onEditCarousel,
  onEditEmptyCard,
  onEditConnect,
  onDeleteCard,
  onUpdateCard,
  onRemoveConnect
}) {
  console.log('[HorizontalSections] Rendering with order:', horizontalOrder);
  console.log('[HorizontalSections] carouselCard:', carouselCard);
  console.log('[HorizontalSections] emptyCards:', emptyCards);

  // Check if carousel should be rendered outside the order loop (for backwards compatibility)
  const shouldRenderCarouselOutside = carouselCard && !horizontalOrder.includes('carousel');

  // Carousel size configuration
  const getCarouselSizes = (cardSize) => {
    const sizes = {
      'S': { width: '140px', height: '100px', totalHeight: '140px' },
      'M': { width: '180px', height: '130px', totalHeight: '180px' },
      'L': { width: '240px', height: '170px', totalHeight: '230px' }
    };
    return sizes[cardSize] || sizes['S'];
  };

  return (
    <>
      {/* Carousel Section - Render at top if not in horizontalOrder */}
      {shouldRenderCarouselOutside && (() => {
        const carouselSize = getCarouselSizes(carouselCard.content?.cardSize);
        return (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
          <ReorderButtons 
            onMoveUp={() => onMoveSection('carousel', 'up')}
            onMoveDown={() => onMoveSection('carousel', 'down')}
            isFirst={true}
            isLast={false}
          />
          <div 
            className="group flex-1"
            style={{ position: 'relative' }}
          >
            <div className="absolute right-0 top-0 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={onEditCarousel} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50">
                <Edit3 className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onDeleteCard(carouselCard); }}
                className="p-2 rounded-full bg-white shadow-md hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
            {carouselCard.title && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', background: cssVars.bgWhite, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  {carouselCard.icon && carouselCard.icon !== '▢' ? carouselCard.icon : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                      <rect x="2" y="6" width="6" height="12" rx="1"/>
                      <rect x="9" y="6" width="6" height="12" rx="1"/>
                      <rect x="16" y="6" width="6" height="12" rx="1"/>
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                  {carouselCard.title || 'carousel'}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '8px', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth' }}>
              {(carouselCard.content?.items || []).length === 0 ? (
                <div onClick={onEditCarousel} style={{ width: carouselSize.width, height: carouselSize.totalHeight, background: cssVars.bgWhite, borderRadius: '16px', border: `2px dashed ${cssVars.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <Plus className="w-6 h-6 text-gray-400 mb-2" />
                  <span style={{ fontSize: '0.75rem', color: cssVars.textMuted }}>Add items</span>
                </div>
              ) : (carouselCard.content?.items || []).map((item, i) => (
                <div key={`carousel-${i}`} style={{ flex: `0 0 ${carouselSize.width}`, scrollSnapAlign: 'start', background: cssVars.bgWhite, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ width: '100%', height: carouselSize.height, overflow: 'hidden', background: item.image || item.linkPreview?.image ? 'transparent' : `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)` }}>
                    {(item.image || item.linkPreview?.image) ? (
                      <img src={item.image || item.linkPreview?.image} alt={item.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '0.8rem', fontWeight: '600', color: cssVars.text, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', margin: 0 }}>
                      {item.title || item.linkPreview?.title || 'Untitled'}
                    </p>
                  </div>
                </div>
              ))}
              {(carouselCard.content?.items || []).length > 0 && (
                <div onClick={onEditCarousel} style={{ flex: '0 0 60px', height: carouselSize.totalHeight, background: cssVars.bg, borderRadius: '16px', border: `2px dashed ${cssVars.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <Plus className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>
            <CardExtrasDisplay extras={carouselCard.extras} />
          </div>
        </div>
      );
      })()}

      {/* Highlights Section - Always rendered first, just below hero */}
      {highlightsCard && (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div style={{ width: '40px' }} /> {/* Spacer for alignment */}
          <div 
            className="group flex-1"
            style={{
              background: cssVars.bgWhite,
              borderRadius: '16px',
              padding: '24px 30px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              position: 'relative'
            }}
          >
            {/* Action buttons */}
            <div className="absolute right-3 top-3 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={onEditHighlights} className="p-2 rounded-full bg-white/80 hover:bg-white shadow-sm">
                <Edit3 className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onDeleteCard(highlightsCard); }} 
                className="p-2 rounded-full bg-white/80 hover:bg-red-50 shadow-sm"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: cssVars.bg,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.1rem'
              }}>
                {highlightsCard.icon || '⭐'}
              </div>
              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                {highlightsCard.title || 'highlights'}
              </span>
            </div>
            {highlightsCard.content?.items?.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {highlightsCard.content.items.map((item, i) => (
                  <li 
                    key={i} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '12px', 
                      marginBottom: i < highlightsCard.content.items.length - 1 ? '12px' : 0 
                    }}
                  >
                    <span style={{ 
                      color: cssVars.accent, 
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      marginTop: '2px'
                    }}>•</span>
                    <span style={{ 
                      fontSize: '0.95rem', 
                      color: cssVars.textSecondary, 
                      lineHeight: '1.6' 
                    }}>{typeof item === 'string' ? item : item.text || ''}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: '0.9rem', color: cssVars.textMuted, fontStyle: 'italic' }}>
                Click to add highlights
              </p>
            )}
            <CardExtrasDisplay extras={highlightsCard.extras} />
          </div>
        </div>
      )}

      {horizontalOrder.map((sectionId, index) => {
        const isFirst = index === 0;
        const isLast = index === horizontalOrder.length - 1;

        // Announcement Section - with glow effect
        if (sectionId === 'announcement') {
          if (!announcementCard) return null;
          console.log('[HorizontalSections] Rendering announcement section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: 'linear-gradient(135deg, #FDF8F3 0%, #FAF0E6 100%)',
                  borderRadius: '16px',
                  padding: '20px 24px',
                  boxShadow: '0 0 15px rgba(217, 179, 147, 0.2), 0 4px 12px rgba(0,0,0,0.03)',
                  position: 'relative',
                  border: '1px solid rgba(217, 179, 147, 0.25)'
                }}
              >
                {/* Action buttons */}
                <div className="absolute right-3 top-3 flex gap-2 z-10">
                  <button onClick={onEditAnnouncement} className="p-2 rounded-full bg-white/80 hover:bg-white shadow-sm">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(announcementCard); }} 
                    className="p-2 rounded-full bg-white/80 hover:bg-red-50 shadow-sm"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '1.5rem' }}>📢</span>
                  <span 
                    className="editable-field"
                    contentEditable 
                    suppressContentEditableWarning 
                    onBlur={(e) => onUpdateCard(announcementCard.id, { content: { text: e.target.textContent } })}
                    style={{ 
                      fontSize: '0.95rem', 
                      fontWeight: '500', 
                      color: '#78716C',
                      flex: 1,
                      outline: 'none'
                    }}
                  >
                    {announcementCard.content?.text || 'Click to add your announcement...'}
                  </span>
                </div>
                <CardExtrasDisplay extras={announcementCard.extras} compact />
              </div>
            </div>
          );
        }

        // Investment Thesis Section
        if (sectionId === 'investmentThesis') {
          if (!investmentThesisCard) return null;
          console.log('[HorizontalSections] Rendering investment thesis section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px 30px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                {/* Action buttons */}
                <div className="absolute right-3 top-3 flex gap-2 z-10">
                  <button onClick={onEditInvestmentThesis} className="p-2 rounded-full bg-white/80 hover:bg-white shadow-sm">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(investmentThesisCard); }} 
                    className="p-2 rounded-full bg-white/80 hover:bg-red-50 shadow-sm"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    📝
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    investment thesis
                  </span>
                </div>

                <p 
                  className="editable-field"
                  contentEditable 
                  suppressContentEditableWarning 
                  onBlur={(e) => onUpdateCard(investmentThesisCard.id, { content: { text: e.target.textContent } })}
                  style={{ 
                    fontSize: '0.95rem', 
                    color: cssVars.textSecondary,
                    lineHeight: '1.7',
                    outline: 'none'
                  }}
                >
                  {investmentThesisCard.content?.text || 'Click to add your investment thesis...'}
                </p>
                <CardExtrasDisplay extras={investmentThesisCard.extras} />
              </div>
            </div>
          );
        }

        // Stats / At A Glance Section
        if (sectionId === 'stats') {
          if (!statsCard) return null;
          console.log('[HorizontalSections] Rendering stats section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                <div className="absolute right-3 top-3 flex gap-2 z-20">
                  <button 
                    onClick={onEditStats} 
                    className="p-2 rounded-lg bg-white shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
                    title="Edit At a Glance"
                  >
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation(); 
                      console.log('[HorizontalSections] Delete stats card clicked');
                      onDeleteCard(statsCard); 
                    }} 
                    className="p-2 rounded-lg bg-white shadow-md border border-red-200 hover:bg-red-50 transition-colors"
                    title="Delete At a Glance"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    {statsCard?.icon || '📊'}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    {statsCard?.title || 'at a glance'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {(statsCard.content?.stats || []).map((stat, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '12px 8px', background: cssVars.bg, borderRadius: '10px' }}>
                      <p style={{ fontSize: '0.7rem', color: cssVars.textMuted, marginBottom: '4px', textTransform: 'lowercase' }}>{stat.label}</p>
                      <p style={{ fontSize: '0.95rem', fontWeight: '600', color: cssVars.text }}>{stat.value}</p>
                    </div>
                  ))}
                </div>
                <CardExtrasDisplay extras={statsCard.extras} />
              </div>
            </div>
          );
        }

        // Connect / Links Section
        if (sectionId === 'connect') {
          console.log('[HorizontalSections] Rendering connect section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px 30px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                {/* Action buttons */}
                <div className="absolute right-4 top-4 flex gap-2 z-10">
                  <button onClick={onEditConnect} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={onRemoveConnect}
                    className="p-2 rounded-full bg-gray-100 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    🔗
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>links</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {connect?.newsletter_url && (
                    <a href={connect.newsletter_url} target="_blank" rel="noopener noreferrer"
                      style={{ 
                        height: '44px', 
                        padding: '0 20px',
                        borderRadius: '100px', 
                        background: cssVars.text, 
                        color: '#fff',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}>
                      Subscribe to Newsletter
                    </a>
                  )}
                  {connect?.podcast_url && (
                    <a href={connect.podcast_url} target="_blank" rel="noopener noreferrer"
                      style={{ 
                        height: '44px', 
                        padding: '0 20px',
                        borderRadius: '100px', 
                        background: cssVars.bg, 
                        color: cssVars.text,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}>
                      🎙️ Podcast
                    </a>
                  )}
                  {connect?.course_url && (
                    <a href={connect.course_url} target="_blank" rel="noopener noreferrer"
                      style={{ 
                        height: '44px', 
                        padding: '0 20px',
                        borderRadius: '100px', 
                        background: cssVars.bg, 
                        color: cssVars.text,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}>
                      📚 Course
                    </a>
                  )}
                  {/* Custom text links */}
                  {connect?.custom_links?.filter(link => link.url && !link.image_url).map((link, idx) => (
                    <a key={`custom-${idx}`} href={link.url} target="_blank" rel="noopener noreferrer"
                      style={{ 
                        height: '44px', 
                        padding: '0 20px',
                        borderRadius: '100px', 
                        background: cssVars.bg, 
                        color: cssVars.text,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        textDecoration: 'none'
                      }}>
                      🔗 {link.title || 'Link'}
                    </a>
                  ))}
                  <button 
                    onClick={onEditConnect}
                    style={{ width: '44px', height: '44px', borderRadius: '50%', background: cssVars.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}
                  >
                    <Plus className="w-5 h-5" style={{ color: cssVars.textMuted }} />
                  </button>
                </div>
                {/* Custom image cards */}
                {connect?.custom_links?.filter(link => link.url && link.image_url).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px' }}>
                    {connect?.custom_links?.filter(link => link.url && link.image_url).map((link, idx) => (
                      <a 
                        key={`card-${idx}`}
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          width: '100px',
                          background: cssVars.bg,
                          borderRadius: '10px',
                          overflow: 'hidden',
                          textDecoration: 'none'
                        }}
                      >
                        <img 
                          src={link.image_url} 
                          alt={link.title}
                          style={{ width: '100%', height: '70px', objectFit: 'cover' }}
                        />
                        <div style={{ padding: '8px', fontSize: '0.75rem', fontWeight: '500', color: cssVars.text, textAlign: 'center' }}>
                          {link.title || 'Link'}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }

        // How I Invest Section
        if (sectionId === 'howIInvest') {
          if (!howIInvestCard) return null;
          console.log('[HorizontalSections] Rendering howIInvest section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px 30px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                <div className="absolute right-4 top-4 flex gap-2 z-10">
                  <button onClick={onEditHowIInvest} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(howIInvestCard); }}
                    className="p-2 rounded-full bg-gray-100 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    {howIInvestCard.icon || '💰'}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    {howIInvestCard.title || 'how I invest'}
                  </span>
                </div>

                {/* Investment Pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
                  {howIInvestCard.content?.lead_follow?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>lead / follow</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.lead_follow.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.primary_stage?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>stage</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.primary_stage.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.check_size?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>check size</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.check_size.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.ownership_target?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>ownership</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.ownership_target.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.hardware_software?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>hw / sw</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.hardware_software.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.b2b_b2c?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>model</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.b2b_b2c.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {howIInvestCard.content?.geo_focus?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'lowercase', letterSpacing: '0.03em', display: 'block', marginBottom: '8px' }}>geo focus</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {howIInvestCard.content.geo_focus.map((v, i) => (
                          <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <CardExtrasDisplay extras={howIInvestCard.extras} />
              </div>
            </div>
          );
        }

        // Investment Focus Section
        if (sectionId === 'investmentFocus') {
          if (!investmentFocusCard) return null;
          console.log('[HorizontalSections] Rendering investmentFocus section');
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px 30px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                <div className="absolute right-4 top-4 flex gap-2 z-10">
                  <button onClick={onEditInvestmentFocus} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(investmentFocusCard); }}
                    className="p-2 rounded-full bg-gray-100 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    {investmentFocusCard.icon || '🎯'}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    {investmentFocusCard.title || 'investment focus'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px' }}>
                  {investmentFocusCard.content?.invest_in?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>I Invest In</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {investmentFocusCard.content.invest_in.map((v, i) => (
                          <span key={i} style={{ background: '#D1FAE5', color: '#065F46', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {investmentFocusCard.content?.do_not_invest_in?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.7rem', color: cssVars.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>I Don&apos;t Invest In</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {investmentFocusCard.content.do_not_invest_in.map((v, i) => (
                          <span key={i} style={{ background: '#FEE2E2', color: '#991B1B', padding: '6px 12px', borderRadius: '100px', fontSize: '0.8rem', fontWeight: '500' }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <CardExtrasDisplay extras={investmentFocusCard.extras} />
              </div>
            </div>
          );
        }

        // Post-Investment Support Section
        if (sectionId === 'postInvestment') {
          if (!postInvestmentCard) return null;
          console.log('[HorizontalSections] Rendering postInvestment section');
          const hasContent = postInvestmentCard.content?.support_areas?.length > 0;
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{
                  background: cssVars.bgWhite,
                  borderRadius: '16px',
                  padding: '24px 30px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative'
                }}
              >
                <div className="absolute right-4 top-4 flex gap-2 z-10">
                  <button onClick={(e) => { e.stopPropagation(); onEditPostInvestment(); }} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(postInvestmentCard); }}
                    className="p-2 rounded-full bg-gray-100 hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                    {postInvestmentCard.icon || '🤝'}
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                    {postInvestmentCard.title || 'post-investment support'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {hasContent ? (
                    postInvestmentCard.content.support_areas.map((area, i) => (
                      <span key={i} style={{ background: '#F5F5F5', padding: '6px 14px', borderRadius: '100px', fontSize: '0.85rem', fontWeight: '500', color: cssVars.text }}>{area}</span>
                    ))
                  ) : (
                    <span style={{ fontSize: '0.85rem', color: cssVars.textMuted, fontStyle: 'italic' }}>Click edit to add support areas...</span>
                  )}
                </div>
                <CardExtrasDisplay extras={postInvestmentCard.extras} />
              </div>
            </div>
          );
        }

        // Carousel Section - Horizontal scrolling cards with no background
        if (sectionId === 'carousel') {
          if (!carouselCard) return null;
          console.log('[HorizontalSections] Rendering carousel section');
          const carouselSize = getCarouselSizes(carouselCard.content?.cardSize);
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{ position: 'relative' }}
              >
                {/* Action buttons - floating top right */}
                <div className="absolute right-0 top-0 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={onEditCarousel} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(carouselCard); }}
                    className="p-2 rounded-full bg-white shadow-md hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                {/* Title row */}
                {carouselCard.title && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bgWhite, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      {carouselCard.icon && carouselCard.icon !== '▢' ? carouselCard.icon : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <rect x="2" y="6" width="6" height="12" rx="1"/>
                          <rect x="9" y="6" width="6" height="12" rx="1"/>
                          <rect x="16" y="6" width="6" height="12" rx="1"/>
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {carouselCard.title || 'carousel'}
                    </span>
                  </div>
                )}

                {/* Carousel items - horizontal scroll, no container background */}
                <div style={{ 
                  display: 'flex', 
                  gap: '16px', 
                  overflowX: 'auto', 
                  paddingBottom: '8px',
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth'
                }}>
                  {(carouselCard.content?.items || []).length === 0 ? (
                    <div 
                      onClick={onEditCarousel}
                      style={{ 
                        width: carouselSize.width,
                        height: carouselSize.totalHeight,
                        background: cssVars.bgWhite,
                        borderRadius: '16px',
                        border: `2px dashed ${cssVars.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Plus className="w-6 h-6 text-gray-400 mb-2" />
                      <span style={{ fontSize: '0.75rem', color: cssVars.textMuted }}>Add items</span>
                    </div>
                  ) : (carouselCard.content?.items || []).map((item, i) => (
                    <div 
                      key={`carousel-loop-${i}`}
                      style={{
                        flex: `0 0 ${carouselSize.width}`,
                        scrollSnapAlign: 'start',
                        background: cssVars.bgWhite,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        cursor: item.url || item.linkPreview?.url ? 'pointer' : 'default'
                      }}
                    >
                      {/* Image */}
                      <div style={{ 
                        width: '100%', 
                        height: carouselSize.height, 
                        overflow: 'hidden',
                        background: item.image || item.linkPreview?.image 
                          ? 'transparent' 
                          : `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`
                      }}>
                        {(item.image || item.linkPreview?.image) ? (
                          <img 
                            src={item.image || item.linkPreview?.image} 
                            alt={item.title || ''} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.style.background = `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`;
                            }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21,15 16,10 5,21"/>
                            </svg>
                          </div>
                        )}
                      </div>
                      {/* Title */}
                      <div style={{ padding: '10px 12px' }}>
                        <p style={{ 
                          fontSize: '0.8rem', 
                          fontWeight: '600', 
                          color: cssVars.text,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          margin: 0
                        }}>
                          {item.title || item.linkPreview?.title || 'Untitled'}
                        </p>
                      </div>
                    </div>
                  ))}
                  {/* Add more button */}
                  {(carouselCard.content?.items || []).length > 0 && (
                    <div 
                      onClick={onEditCarousel}
                      style={{ 
                        flex: '0 0 60px',
                        height: carouselSize.totalHeight,
                        background: cssVars.bg,
                        borderRadius: '16px',
                        border: `2px dashed ${cssVars.border}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </div>
                <CardExtrasDisplay extras={carouselCard.extras} />
              </div>
            </div>
          );
        }

        // Empty Card (horizontal) - Flexible content card
        if (sectionId.startsWith('empty_')) {
          const emptyCardId = sectionId.replace('empty_', '');
          const emptyCard = emptyCards.find(c => c.id === emptyCardId);
          if (!emptyCard) return null;
          
          return (
            <div key={sectionId} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
              <ReorderButtons 
                onMoveUp={() => onMoveSection(sectionId, 'up')}
                onMoveDown={() => onMoveSection(sectionId, 'down')}
                isFirst={isFirst}
                isLast={isLast}
              />
              <div 
                className="group flex-1"
                style={{ 
                  position: 'relative',
                  background: cssVars.bgWhite,
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                {/* Action buttons */}
                <div className="absolute right-4 top-4 flex gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onEditEmptyCard(emptyCard)} className="p-2 rounded-full bg-gray-50 hover:bg-gray-100">
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDeleteCard(emptyCard); }}
                    className="p-2 rounded-full bg-gray-50 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>

                {/* Title row */}
                {emptyCard.title && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '36px', height: '36px', background: cssVars.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                      {emptyCard.icon || '📝'}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
                      {emptyCard.title}
                    </span>
                  </div>
                )}

                {/* Content fields - group consecutive links */}
                {emptyCard.content?.fields?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {(() => {
                      // Group consecutive links together
                      const groupedFields = [];
                      let currentLinkGroup = [];
                      
                      emptyCard.content.fields.forEach((field, i) => {
                        if (field.type === 'link' && field.url) {
                          currentLinkGroup.push(field);
                        } else {
                          if (currentLinkGroup.length > 0) {
                            groupedFields.push({ type: 'linkGroup', links: [...currentLinkGroup] });
                            currentLinkGroup = [];
                          }
                          groupedFields.push(field);
                        }
                      });
                      
                      // Don't forget trailing links
                      if (currentLinkGroup.length > 0) {
                        groupedFields.push({ type: 'linkGroup', links: currentLinkGroup });
                      }
                      
                      return groupedFields.map((item, i) => (
                        <div key={i}>
                          {item.type === 'text' && item.value && (
                            <p style={{ 
                              margin: 0, 
                              fontSize: '0.95rem', 
                              color: cssVars.text,
                              fontWeight: item.isBold ? '600' : '400',
                              lineHeight: '1.6'
                            }}>
                              {item.value}
                            </p>
                          )}
                          {item.type === 'tags' && item.value?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {item.value.map((tag, ti) => (
                                <span 
                                  key={ti}
                                  style={{
                                    padding: '6px 14px',
                                    background: cssVars.accentLight,
                                    color: cssVars.accent,
                                    borderRadius: '100px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {item.type === 'linkGroup' && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {item.links.map((link, li) => (
                                <a 
                                  key={link.id || li}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 16px',
                                    background: cssVars.bg,
                                    border: `1px solid ${cssVars.border}`,
                                    borderRadius: '12px',
                                    color: cssVars.accent,
                                    fontSize: '0.9rem',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                                  </svg>
                                  {link.value || link.url}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  <div 
                    onClick={() => onEditEmptyCard(emptyCard)}
                    style={{ 
                      padding: '32px',
                      textAlign: 'center',
                      color: cssVars.textMuted,
                      fontSize: '0.9rem',
                      border: `2px dashed ${cssVars.border}`,
                      borderRadius: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Click to add content
                  </div>
                )}
              </div>
            </div>
          );
        }

        return null;
      })}
    </>
  );
}
